import { Badge } from '@kerfjs/ui/badge';
import { Button } from '@kerfjs/ui/button';
import { List } from '@kerfjs/ui/list';
import { ListInsetControl } from '@kerfjs/ui/list-inset-control';
import { ListInsetText } from '@kerfjs/ui/list-inset-text';
import { Row } from '@kerfjs/ui/row';
import { FieldLabel, Text } from '@kerfjs/ui/text';
import { TextField } from '@kerfjs/ui/text-field';
import { type SafeHtml } from 'kerfjs';

import { canEditNote, type DemandNote, NOTE_MAX_LENGTH, notesInScope } from '../../../shared/demand-notes.js';
import { formatNoteTime } from '../../lib/format.js';
import { ListMarkdownPreview } from '../list-markdown-preview.js';

/**
 * One append-only notes list (HS2-E33XQD): the demand's own notes (in Documents) or one line item's
 * (HS2-JRQT8A). A heading with the note count, the notes oldest first with author and time, the
 * author's Edit on their newest note within a minute of posting, and a composer that appends.
 * Read-only lists (a closed or cancelled demand) show the notes without the composer or Edit.
 */
export function DemandNotes({
  notes,
  lineItemId = null,
  readOnly,
  memberId,
  editingNoteId = null,
  now = Date.now(),
  headingId,
}: {
  /** Every note on the demand; this list shows the ones in its scope. */
  notes: readonly DemandNote[];
  lineItemId?: string | null;
  readOnly: boolean;
  memberId: string | null | undefined;
  /** The note open in its editor, if any. */
  editingNoteId?: string | null;
  now?: number;
  headingId: string;
}): SafeHtml {
  const list = notesInScope(notes, lineItemId);
  const scope = lineItemId ?? '';
  return (
    <div role="group" aria-labelledby={headingId} class="demand-notes" data-notes-scope={scope || 'demand'}>
      <List gap="xs">
        <ListInsetControl sides="trl">
          <Row gap="xs" vAlign="middle">
            <FieldLabel id={headingId}>Notes</FieldLabel>
            <Badge tone="neutral" shape="pill" size="compact" label={`${String(list.length)} notes`}>
              {list.length}
            </Badge>
          </Row>
        </ListInsetControl>
        {list.length === 0 ? (
          <ListInsetText sides="rl">
            <Text variant="span" tone="quiet" size="compact">
              {readOnly ? 'No notes.' : 'No notes yet.'}
            </Text>
          </ListInsetText>
        ) : null}
        {list.map((note) => {
          const editable = !readOnly && canEditNote(note, list, memberId, now);
          return (
            <div class="demand-note" data-key={note.id} data-note-id={note.id}>
              <List gap="2xs">
                <ListInsetText sides="rl">
                  <Row gap="xs" vAlign="middle" wrap>
                    <Text variant="span" size="compact">
                      <strong>{note.authorName ?? 'Someone'}</strong>
                    </Text>
                    <Text variant="span" tone="quiet" size="compact">
                      {formatNoteTime(note.createdAt)}
                      {note.editedAt !== null ? ' · edited' : null}
                    </Text>
                  </Row>
                </ListInsetText>
                {editingNoteId === note.id && editable ? (
                  <ListInsetControl sides="rl">
                    <List gap="xs">
                      <TextField
                        multiline
                        name="note-edit-body"
                        label="Edit Note"
                        rows={3}
                        maxLength={NOTE_MAX_LENGTH}
                        value={note.body}
                        rootAttributes={{ 'data-unsaved-ignore': '' }}
                      />
                      <Row gap="xs" hAlign="right">
                        <Button emphasis="plain" tone="neutral" size="compact" action="note-edit-cancel">
                          Cancel
                        </Button>
                        <Button
                          emphasis="primary"
                          tone="brand"
                          size="compact"
                          action="note-edit-save"
                          rootAttributes={{ 'data-note-id': note.id }}
                        >
                          Save
                        </Button>
                      </Row>
                    </List>
                  </ListInsetControl>
                ) : (
                  <ListMarkdownPreview markdown={note.body} emptyLabel="" compact />
                )}
                {editable && editingNoteId !== note.id ? (
                  <ListInsetControl sides="rl">
                    <Row>
                      <Button
                        emphasis="plain"
                        tone="brand"
                        size="compact"
                        action="note-edit"
                        rootAttributes={{ 'data-note-id': note.id }}
                      >
                        Edit
                      </Button>
                    </Row>
                  </ListInsetControl>
                ) : null}
              </List>
            </div>
          );
        })}
        {readOnly ? null : (
          <ListInsetControl sides="rl">
            <List gap="xs">
              <TextField
                multiline
                name="note-compose"
                label="Add a Note"
                rows={2}
                maxLength={NOTE_MAX_LENGTH}
                placeholder="Markdown supported"
                rootAttributes={{ 'data-note-line': scope, 'data-unsaved-ignore': '' }}
              />
              <Row hAlign="right">
                <Button
                  emphasis="primary"
                  tone="brand"
                  size="compact"
                  action="note-add"
                  rootAttributes={{ 'data-note-line': scope }}
                >
                  Add Note
                </Button>
              </Row>
            </List>
          </ListInsetControl>
        )}
      </List>
    </div>
  );
}
